import { useEffect, useRef, useState } from 'react';

import { allegati as apiAllegati } from '../api/scadenziario.ts';
import type { RegoleAllegati } from '../api/tipi-dati.ts';
import { motivoRifiuto } from './allegatiCreazione.ts';

/**
 * Il campo per allegare file **mentre si crea** una scadenza.
 *
 * ⚠️ Qui non si carica niente: i file restano in memoria e li spedisce
 * `DialogoScadenza` subito dopo che la scadenza è nata, perché la rotta è
 * `POST /scadenze/:id/allegati` e prima del salvataggio quell'id non esiste.
 *
 * Sulla scadenza già esistente ci pensa `PannelloAllegati` dalla sua scheda,
 * che carica sul momento: sono due componenti perché sono due situazioni
 * diverse, non per distrazione.
 */
export function AllegatiInAttesa({
  scelti,
  onCambia,
}: {
  scelti: File[];
  onCambia: (file: File[]) => void;
}) {
  const [regole, setRegole] = useState<RegoleAllegati | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const campoFile = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiAllegati.regole().then(setRegole).catch(() => {
      // Senza regole non si blocca niente: il limite vero sta sul server e
      // resta lì. Qui si perde solo la cortesia di dirlo prima di spedire.
    });
  }, []);

  function aggiungi(lista: FileList | null) {
    if (lista === null || lista.length === 0) return;
    setErrore(null);

    // Si accumula su una copia e non su `scelti`: dentro lo stesso giro il
    // secondo file deve vedere il primo, o due file uguali entrerebbero
    // entrambi e il tetto di numero non scatterebbe mai.
    let correnti = [...scelti];
    for (const file of Array.from(lista)) {
      const motivo = motivoRifiuto(file, correnti, regole);
      if (motivo !== null) { setErrore(motivo); continue; }
      correnti = [...correnti, file];
    }
    onCambia(correnti);

    // Si svuota il campo, o riscegliere lo stesso file non emetterebbe nessun
    // evento e sembrerebbe che il pulsante non funzioni.
    if (campoFile.current !== null) campoFile.current.value = '';
  }

  return (
    <div className="campo">
      <label htmlFor="allegati">Allegati <span className="tenue">facoltativi</span></label>
      <input
        id="allegati"
        ref={campoFile}
        type="file"
        multiple
        onChange={(e) => aggiungi(e.target.files)}
      />

      {errore !== null && <p className="aiuto errore">{errore}</p>}

      {scelti.length > 0 && (
        <ul className="allegati-in-attesa">
          {scelti.map((f) => (
            <li key={f.name}>
              <span className="allegato-nome">{f.name}</span>
              <button
                type="button"
                className="bottone-piatto"
                onClick={() => onCambia(scelti.filter((x) => x.name !== f.name))}
              >
                Togli
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ Detto qui e non dopo: chi sceglie un file grosso deve sapere prima
          di premere «Crea» che partirà un caricamento, non dopo. */}
      <p className="aiuto">
        {scelti.length === 0
          ? 'Verranno caricati subito dopo aver creato la scadenza.'
          : `${scelti.length === 1 ? 'Sarà caricato' : `Saranno caricati`} alla creazione della scadenza.`}
        {regole !== null && ` Al massimo ${regole.massimoLeggibile} per file.`}
      </p>
    </div>
  );
}
